import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { DateRange, Icon } from '@percent/lemonade'
import { DisbursementsDateRangeFilterProps } from './DisbursementsDateRangeFilter.types'

/**
 * Mirrors the trigger Lemonade's FilterList renders, so the two date filters sit in the same filter
 * row as the Foundation / Paid by / Status pills at the same height, padding and font. Lemonade does
 * not export that trigger on its own — it is bound to the FilterList listbox — so it is repeated
 * here against the same tokens rather than approximated.
 */
const StyledTrigger = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xxs};
    margin: 0;
    padding: ${theme.spacing.xxs} ${theme.spacing.xs};
    background: none;
    border: none;
    border-radius: ${theme.radii.interactive};
    font-size: ${theme.fontSizes.bodySmall};
    font-weight: ${theme.fontWeights.medium};
    white-space: nowrap;
    cursor: pointer;

    :hover {
      background: ${theme.semantic.backgroundHover};
    }

    /* primary400 is the one value here with no semantic alias — every brand-ish semantic token
       points at purple400 instead, so using one would tint these pills differently from the
       FilterList pills beside them. Deliberately deprecated until Lemonade migrates that palette. */
    :focus {
      outline: 1px solid ${theme.colors.primary400};
    }

    :active {
      background: ${theme.semantic.backgroundInfoSubtle};
      color: ${theme.colors.primary400};
    }
  `}
`

/**
 * Matches FilterList's count badge rather than the design's literal purple: these pills sit beside
 * the Paid by and Status ones, and the app still renders Lemonade's legacy palette, so taking the
 * brand token here would make this badge the odd one out on the same row.
 */
const StyledBadge = styled.span`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.buttonText};
    padding: 1px ${theme.spacing.xxs};
    background: ${theme.colors.primary400};
    color: ${theme.semantic.contentInvert};
    border-radius: ${theme.borderRadii.pill};
  `}
`

export function DisbursementsDateRangeFilter({ label, from, to, onChange, testId }: DisbursementsDateRangeFilterProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const toggle = useCallback(() => setIsOpen(open => !open), [])

  // A range only filters once both ends are set — a lone bound is dropped before the request, so
  // badging it would claim a filter that is not applied.
  const isFiltering = !!from && !!to

  return (
    <div data-testid={testId}>
      <DateRange
        open={isOpen}
        setOpen={setIsOpen}
        queryParams={{ startDate: from, endDate: to }}
        setQueryParams={({ startDate, endDate }) => {
          onChange({ from: startDate, to: endDate })
          setIsOpen(false)
        }}
        applyButtonText={t('button.apply')}
        clearButtonText={t('button.clear')}
        withinPortal={false}
      >
        <StyledTrigger type="button" onClick={toggle}>
          <span>{label}</span>
          {isFiltering && <StyledBadge data-testid={`${testId}-badge`}>1</StyledBadge>}
          <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={3} color="black" />
        </StyledTrigger>
      </DateRange>
    </div>
  )
}
